'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardCheck, X, ChevronRight } from 'lucide-react';

const ONBOARDING_SURVEY_DISMISSED_KEY = 'rr_onboarding_survey_dismissed';

interface OnboardingSurveyProps {
  userType: 'worker' | 'contractor';
  userName?: string;
}

const QUESTIONS = {
  contractor: [
    {
      key: 'how_found',
      label: 'How did you find RateRight?',
      options: ['Google search', 'Social media', 'Friend/colleague', 'QR code/poster', 'Job board', 'Other'],
    },
    {
      key: 'why_joining',
      label: 'What made you sign up?',
      options: ['Need workers fast', 'Cheaper than agencies', 'AI matching sounded good', 'Someone recommended it', 'Just checking it out'],
    },
    {
      key: 'biggest_pain',
      label: "What's your biggest hiring headache?",
      type: 'text' as const,
      placeholder: 'e.g. Can never find labourers on short notice...',
    },
  ],
  worker: [
    {
      key: 'how_found',
      label: 'How did you find RateRight?',
      options: ['Google search', 'Social media', 'Friend/colleague', 'QR code/poster', 'Hostel', 'Job board', 'Other'],
    },
    {
      key: 'why_joining',
      label: 'What made you sign up?',
      options: ['Looking for construction work', 'Better than job boards', 'Someone recommended it', 'Quick signup', 'Just checking it out'],
    },
    {
      key: 'biggest_pain',
      label: "What's your biggest job-finding pain?",
      type: 'text' as const,
      placeholder: 'e.g. Hard to find short-term construction work...',
    },
  ],
};

export function OnboardingSurvey({ userType, userName }: OnboardingSurveyProps) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [textInput, setTextInput] = useState('');

  const questions = QUESTIONS[userType];

  useEffect(() => {
    // Don't show if already dismissed or completed
    const dismissed = localStorage.getItem(ONBOARDING_SURVEY_DISMISSED_KEY);
    if (dismissed) return;

    // Check if already completed
    fetch('/api/success-tracking/survey?type=onboarding')
      .then(r => r.json())
      .then(data => {
        if (!data.completed) {
          // Small delay so dashboard loads first
          setTimeout(() => setVisible(true), 2000);
        }
      })
      .catch(() => {
        // If check fails, don't show — not critical
      });
  }, []);

  const dismiss = () => {
    localStorage.setItem(ONBOARDING_SURVEY_DISMISSED_KEY, 'true');
    setVisible(false);
  };

  const selectOption = (questionKey: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionKey]: value }));
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      submitSurvey({ ...answers, [questionKey]: value });
    }
  };

  const submitTextAnswer = () => {
    if (!textInput.trim()) return;
    const q = questions[step];
    const updatedAnswers = { ...answers, [q.key]: textInput.trim() };
    setAnswers(updatedAnswers);

    if (step < questions.length - 1) {
      setTextInput('');
      setStep(step + 1);
    } else {
      submitSurvey(updatedAnswers);
    }
  };

  const submitSurvey = async (finalAnswers: Record<string, string>) => {
    setSubmitting(true);
    try {
      await fetch('/api/success-tracking/survey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          survey_type: 'onboarding',
          responses: finalAnswers,
        }),
      });
      setDone(true);
      localStorage.setItem(ONBOARDING_SURVEY_DISMISSED_KEY, 'true');
      // Auto-hide after 2 seconds
      setTimeout(() => setVisible(false), 2000);
    } catch {
      // Don't block the user — survey is optional
      setVisible(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  const currentQuestion = questions[step];

  return (
    <Card className="border-blue-500/30 bg-blue-500/5 relative overflow-hidden">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1 rounded-md hover:bg-white/10 transition text-muted-foreground"
        aria-label="Dismiss survey"
      >
        <X className="h-4 w-4" />
      </button>

      <CardHeader className="pb-2 pr-10">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardCheck className="h-5 w-5 text-blue-500" />
          {done ? 'Thanks!' : `Quick question${userName ? `, ${userName}` : ''}`}
        </CardTitle>
        {!done && (
          <CardDescription className="text-sm">
            {step + 1} of {questions.length} · Helps us build what you need
          </CardDescription>
        )}
      </CardHeader>

      <CardContent>
        {done ? (
          <p className="text-sm text-muted-foreground">Your answers help us build a better RateRight 🙌</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium">{currentQuestion.label}</p>

            {'options' in currentQuestion && currentQuestion.options ? (
              <div className="grid gap-2">
                {currentQuestion.options.map((option) => (
                  <Button
                    key={option}
                    variant="outline"
                    className="justify-between h-auto py-2.5 px-3 text-sm text-left"
                    onClick={() => selectOption(currentQuestion.key, option)}
                    disabled={submitting}
                  >
                    {option}
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </Button>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  rows={2}
                  placeholder={'placeholder' in currentQuestion ? currentQuestion.placeholder : ''}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  maxLength={500}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={submitTextAnswer}
                    disabled={!textInput.trim() || submitting}
                    className="flex-1"
                    size="sm"
                  >
                    {step < questions.length - 1 ? 'Next' : 'Done'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (step < questions.length - 1) {
                        setStep(step + 1);
                      } else {
                        submitSurvey(answers);
                      }
                    }}
                    disabled={submitting}
                  >
                    Skip
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
